import { useSyncExternalStore } from 'react';
import type { SessionUser } from '@web/shared';

/**
 * Client session store. The bearer token + last-known user live in
 * localStorage; `lib/api.ts` reads the token for every request. Components read
 * reactive state via `useSession()`.
 */
const TOKEN_KEY = 'nm.token';
const USER_KEY = 'nm.user';

let cachedUser: SessionUser | null = readUser();
const listeners = new Set<() => void>();

function readUser(): SessionUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as SessionUser) : null;
  } catch {
    return null;
  }
}

function emit() {
  for (const l of listeners) l();
}

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setSession(token: string, user: SessionUser): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch {
    /* private mode */
  }
  cachedUser = user;
  emit();
}

/** Swap just the bearer token (e.g. after a password change re-issues one). */
export function setToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* ignore */
  }
  emit();
}

export function updateSessionUser(user: SessionUser): void {
  try {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch {
    /* ignore */
  }
  cachedUser = user;
  emit();
}

export function clearSession(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch {
    /* ignore */
  }
  cachedUser = null;
  emit();
}

export interface SessionState {
  user: SessionUser | null;
  isAuthed: boolean;
  isOnboarded: boolean;
  isAdmin: boolean;
}

export function useSession(): SessionState {
  const user = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => cachedUser,
    () => cachedUser,
  );
  return {
    user,
    isAuthed: !!user,
    isOnboarded: !!user?.onboarded,
    isAdmin: !!user?.adminRole,
  };
}

/** Snapshot for router `beforeLoad` guards (non-reactive). */
export function sessionSnapshot(): SessionState {
  const user = cachedUser;
  return {
    user,
    isAuthed: !!user,
    isOnboarded: !!user?.onboarded,
    isAdmin: !!user?.adminRole,
  };
}
